import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useToast } from "../context/ToastContext";
import { useI18n, useT } from "../i18n";
import Pic from "./pics";

// Streamer mode: when your screen is on stream, the secret things (your word,
// role or hand, the Code Words key, other players' Who Am I? cards, the room
// code) move to a second "secret window" you keep on a screen you don't
// stream. The window belongs to this page: games draw into it with React
// portals (<Secret>), so its buttons (Mafia's night pick, Last Card's hand)
// work like any other.

const KEY = "partycade.streamer";
const WINDOW_NAME = "partycade-secret";
// A tiny static page (public/streamer.html): fonts load there like on any
// page, and no ads or analytics run in it.
const WINDOW_URL = "/streamer.html";

const StreamerContext = createContext({ on: false, open: false, slots: null });
export const useStreamer = () => useContext(StreamerContext);

function readOn() {
  try {
    return localStorage.getItem(KEY) === "1";
  } catch {
    return false;
  }
}

// Copies the page's styles into the secret window, and keeps copying new
// ones (each game's styles load when it first starts).
function mirrorStyles(win) {
  const copy = (node) => {
    if (node.matches?.('style, link[rel="stylesheet"]')) win.document.head.appendChild(node.cloneNode(true));
  };
  document.head.querySelectorAll('style, link[rel="stylesheet"]').forEach(copy);
  const watcher = new MutationObserver((changes) => changes.forEach((c) => c.addedNodes.forEach(copy)));
  watcher.observe(document.head, { childList: true });
  return () => watcher.disconnect();
}

export function StreamerProvider({ children }) {
  const { t, lang, dir } = useI18n();
  const { toast } = useToast();
  const [on, setOnState] = useState(readOn);
  const [win, setWin] = useState(null); // { window, root }
  const [slots, setSlots] = useState(null);
  const [count, setCount] = useState(0);
  const winRef = useRef(null);
  const stopStyles = useRef(null);

  const detach = useCallback(() => {
    stopStyles.current?.();
    stopStyles.current = null;
    winRef.current = null;
    setWin(null);
    setSlots(null);
  }, []);

  const openWindow = useCallback(() => {
    const open = winRef.current;
    if (open && !open.closed) {
      open.focus();
      return true;
    }
    // Opens the page (or reloads it, if a window from before is still there).
    const w = window.open(WINDOW_URL, WINDOW_NAME, "popup,width=480,height=760");
    if (!w) {
      toast({ emoji: "block", kind: "error", title: t("Your browser blocked the secret window"), body: t("Allow pop-ups for PartyCade, then try again.") });
      return false;
    }
    winRef.current = w;
    // Moves in once the page has loaded.
    const attach = () => {
      if (winRef.current !== w || w.closed) return;
      let root = null;
      try {
        if (w.document.readyState === "complete") root = w.document.querySelector(".streamer-root");
      } catch {}
      if (!root) return void setTimeout(attach, 50);
      stopStyles.current?.();
      stopStyles.current = mirrorStyles(w);
      w.addEventListener("pagehide", detach);
      setWin({ window: w, root });
    };
    attach();
    w.focus();
    return true;
  }, [detach, t, toast]);

  // Notice when the streamer closes the window.
  useEffect(() => {
    if (!win) return;
    const id = setInterval(() => win.window.closed && detach(), 1000);
    return () => clearInterval(id);
  }, [win, detach]);

  // The window follows the page's language and title.
  useEffect(() => {
    if (!win) return;
    const d = win.window.document;
    d.documentElement.lang = lang;
    d.documentElement.dir = dir;
    d.title = `${t("Secret window")} - PartyCade`;
  }, [win, lang, dir, t]);

  // If this page closes or reloads, the window can't update any more: say so.
  const lostText = t("The game page was closed or reloaded. Open the secret window again from the game.");
  useEffect(() => {
    const onHide = () => {
      const w = winRef.current;
      if (!w || w.closed) return;
      const p = w.document.createElement("p");
      p.className = "streamer-lost";
      p.textContent = lostText;
      w.document.body.replaceChildren(p);
    };
    window.addEventListener("pagehide", onHide);
    return () => window.removeEventListener("pagehide", onHide);
  }, [lostText]);

  const setOn = useCallback(
    (value) => {
      setOnState(value);
      try {
        localStorage.setItem(KEY, value ? "1" : "0");
      } catch {}
      if (value) {
        toast({
          emoji: "video",
          title: t("Streamer mode on"),
          body: t("Your secret words, cards and the room code now show in a separate window. Drag it to a screen you don't stream."),
          duration: 8000,
        });
        openWindow();
      } else {
        const w = winRef.current;
        detach();
        if (w && !w.closed) w.close();
      }
    },
    [openWindow, detach, t, toast]
  );

  const register = useCallback((n) => setCount((c) => c + n), []);
  const value = useMemo(() => ({ on, setOn, open: !!win, openWindow, slots, register }), [on, setOn, win, openWindow, slots, register]);

  return (
    <StreamerContext.Provider value={value}>
      {children}
      {on &&
        win &&
        createPortal(
          <>
            <header className="streamer-head">
              <strong>{t("Secret window")}</strong>
              <span>{t("Keep this window off stream.")}</span>
            </header>
            <div className="streamer-slots" ref={setSlots} />
            {count === 0 && <p className="streamer-empty">{t("Nothing secret right now. Your word, role or cards show up here during a game.")}</p>}
          </>,
          win.root
        )}
    </StreamerContext.Provider>
  );
}

// Streamer mode on the main screen: the secret, blurred (it can't be clicked
// or focused), with a note that it's in the secret window.
function Blurred({ inline, children }) {
  const t = useT();
  const { open, openWindow } = useStreamer();
  const Tag = inline ? "span" : "div";
  return (
    <Tag className={`streamer-blurred ${inline ? "inline" : ""}`}>
      <Tag className="streamer-blur" inert aria-hidden="true">
        {children}
      </Tag>
      <Tag className="streamer-over">
        {open ? (
          <span className="streamer-note">
          <Pic name="video" />
          {!inline && ` ${t("On your secret screen")}`}
        </span>
        ) : (
          <button className="btn btn-sm btn-primary" onClick={openWindow}>
            <Pic name="video" />
        {!inline && ` ${t("Open secret window")}`}
          </button>
        )}
      </Tag>
    </Tag>
  );
}

// Something only you should see. Normally it shows right here; in streamer
// mode it shows in the secret window, and here it's blurred. Blur hides
// words but not colours or shapes, so when those give something away (the
// impostor's dark card, a Mafia role's colour, where the Vibe Dial target
// is), `cover` is what gets blurred instead: the same for everyone.
// `label` names it in the secret window ("Your word"), `order` sorts the
// secret window (lower first), `quiet` leaves nothing on the page, and
// `streamOnly` shows it only in the secret window (never on the page).
export function Secret({ children, label, cover, inline = false, order = 0, quiet = false, streamOnly = false }) {
  const { on, slots, register } = useStreamer();
  const [box, setBox] = useState(null);
  useLayoutEffect(() => {
    if (!on || !slots) return;
    const el = slots.ownerDocument.createElement("section");
    el.className = "streamer-slot";
    el.style.order = order;
    slots.appendChild(el);
    setBox(el);
    register(1);
    return () => {
      el.remove();
      setBox(null);
      register(-1);
    };
  }, [on, slots, order, register]);
  if (!on) return streamOnly ? null : children;
  return (
    <>
      {!quiet && !streamOnly && <Blurred inline={inline}>{cover ?? children}</Blurred>}
      {box &&
        createPortal(
          <div className="streamer-block panel">
            {label && <h3>{label}</h3>}
            {children}
          </div>,
          box
        )}
    </>
  );
}

// The labelled switch on the home page, to turn it on before making a room.
export function StreamerSwitch() {
  const t = useT();
  const { on, setOn } = useStreamer();
  return (
    <button className={`streamer-switch ${on ? "on" : ""}`} onClick={() => setOn(!on)} aria-pressed={on}>
      <span className="streamer-switch-track" aria-hidden>
        <span />
      </span>
      <span>
        <strong>
          <Pic name="video" /> {on ? t("Streamer mode: on") : t("Streamer mode: off")}
        </strong>
        <small>{t("Streaming? Your words, cards and the room code stay off your stream.")}</small>
      </span>
    </button>
  );
}

// The header's 🎥 button.
export function StreamerToggle() {
  const t = useT();
  const { on, setOn } = useStreamer();
  const label = on ? t("Streamer mode: on") : t("Streamer mode: off");
  return (
    <button className={`btn btn-sm btn-ghost streamer-toggle ${on ? "on" : ""}`} onClick={() => setOn(!on)} title={label} aria-label={label} aria-pressed={on}>
      <Pic name="video" />
    </button>
  );
}
